import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Download, Percent, Stethoscope, Timer } from 'lucide-react';
import { toast } from 'sonner';
import type { AbsenceListItem, AbsenceType, LeaveRegisterRow } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useHrApi, SessionExpiredError } from '@/services/api';
import {
  ABSENCE_TYPE_META, absenceStatusLabel, absenceTypeLabel, addDays, downloadCsv, eachDay,
  formatDays, isWeekend, monthEnd, monthStart, todayYmd,
} from '@/lib/hr';
import { t } from '@/i18n';
import { AbsenceMonthlyChart, AbsenceTopChart } from './AbsenceCharts';
import { EmployeeStatsTable } from './EmployeeStatsTable';
import { employeeStatsRows, monthlyAbsences, topEmployeesByAbsence } from './hrStatsAggregation';

/**
 * HR statistics: absence rate for the current month (approved absence
 * person-days over scheduled working days), sick days YTD, pending queue
 * size, who is away in the next 7 days, a per-type breakdown and a CSV
 * export of the full year.
 */
export function HrStats({ nip, headcount, holidays }: {
  nip: string;
  headcount: number;
  holidays: Map<string, string>;
}) {
  const { listAbsences, leaveRegister } = useHrApi();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const today = todayYmd();
  const [absences, setAbsences] = useState<AbsenceListItem[]>([]);
  const [register, setRegister] = useState<LeaveRegisterRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      listAbsences(nip, { from: `${year}-01-01`, to: `${year}-12-31` }),
      leaveRegister(nip, year),
    ])
      .then(([absenceRows, registerRows]) => {
        if (!mounted) return;
        setAbsences(absenceRows);
        setRegister(registerRows);
      })
      .catch((error) => {
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [listAbsences, leaveRegister, nip, year]);

  const stats = useMemo(() => {
    const mStart = monthStart(today);
    const mEnd = monthEnd(today);
    const workingDaysInMonth = eachDay(mStart, mEnd)
      .filter((d) => !isWeekend(d) && !holidays.has(d)).length;

    let absenceDaysThisMonth = 0;
    let sickDaysYtd = 0;
    let pendingCount = 0;
    const outNext7 = new Set<string>();
    const byType = new Map<AbsenceType, number>();
    const next7End = addDays(today, 7);

    for (const a of absences) {
      if (a.status === 'pending') pendingCount += 1;
      if (a.status !== 'approved') continue;

      if (a.type === 'sick') sickDaysYtd += a.days;
      byType.set(a.type, (byType.get(a.type) ?? 0) + a.days);

      if (a.type !== 'remote_work' && a.dateFrom <= mEnd && a.dateTo >= mStart) {
        const from = a.dateFrom < mStart ? mStart : a.dateFrom;
        const to = a.dateTo > mEnd ? mEnd : a.dateTo;
        absenceDaysThisMonth += eachDay(from, to)
          .filter((d) => !isWeekend(d) && !holidays.has(d)).length;
      }
      if (a.type !== 'remote_work' && a.dateFrom <= next7End && a.dateTo >= today) {
        outNext7.add(a.employeeId);
      }
    }

    const scheduled = headcount * workingDaysInMonth;
    const rate = scheduled > 0 ? (absenceDaysThisMonth / scheduled) * 100 : 0;
    const byTypeSorted = [...byType.entries()]
      .filter(([type]) => type !== 'remote_work')
      .sort((a, b) => b[1] - a[1]);
    const maxByType = byTypeSorted[0]?.[1] ?? 0;

    return { rate, sickDaysYtd, pendingCount, outNext7: outNext7.size, byTypeSorted, maxByType };
  }, [absences, headcount, holidays, today]);

  const exportCsv = () => {
    downloadCsv(`nieobecnosci-${year}.csv`, [
      [
        t('hr.sick.employee'), t('hr.request.type'), t('hr.request.dateFrom'), t('hr.request.dateTo'),
        t('hr.sick.days'), 'Status',
      ],
      ...absences.map((a) => [
        a.employeeName, absenceTypeLabel(a.type), a.dateFrom, a.dateTo, a.days, absenceStatusLabel(a.status),
      ]),
    ]);
  };

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-8 w-28" aria-label={t('hr.stats.yearLabel')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Percent className="size-4" />}
          label={t('hr.stats.absenceRate')}
          value={`${stats.rate.toFixed(1)}%`}
          hint={t('hr.stats.ofWorkingTime')}
        />
        <StatCard
          icon={<Stethoscope className="size-4" />}
          label={t('hr.stats.sickDaysYtd', { year })}
          value={formatDays(stats.sickDaysYtd)}
        />
        <StatCard
          icon={<Timer className="size-4" />}
          label={t('hr.stats.pendingRequests')}
          value={String(stats.pendingCount)}
          highlight={stats.pendingCount > 0}
        />
        <StatCard
          icon={<CalendarClock className="size-4" />}
          label={t('hr.stats.outNext7')}
          value={t('hr.stats.people', { count: stats.outNext7 })}
        />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-2">
        <AbsenceMonthlyChart rows={monthlyAbsences(absences, year)} year={year} />
        <AbsenceTopChart rows={topEmployeesByAbsence(absences)} year={year} />
      </div>

      <EmployeeStatsTable rows={employeeStatsRows(register, absences, year, holidays)} year={year} />

      <div className="rounded-lg border p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t('hr.stats.byType', { year })}</h3>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={absences.length === 0}>
            <Download className="size-4" />
            {t('hr.stats.exportCsv')}
          </Button>
        </div>
        {stats.byTypeSorted.length === 0 ? (
          <p className="text-muted-foreground py-4 text-center text-sm">{t('hr.stats.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {stats.byTypeSorted.map(([type, days]) => (
              <li key={type} className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground w-44 shrink-0 truncate sm:w-56">
                  {absenceTypeLabel(type)}
                </span>
                <span className="bg-muted h-4 min-w-0 flex-1 overflow-hidden rounded-sm">
                  <span
                    className={cn('block h-full rounded-sm', ABSENCE_TYPE_META[type].dot)}
                    style={{ width: `${stats.maxByType > 0 ? (days / stats.maxByType) * 100 : 0}%` }}
                  />
                </span>
                <span className="w-16 shrink-0 text-right font-medium tabular-nums">{formatDays(days)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, hint, highlight }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <div className={cn('rounded-lg border p-4', highlight && 'border-amber-500/50 bg-amber-500/5')}>
      <p className="text-muted-foreground flex items-center gap-1.5 text-xs">{icon}{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}
